import { useState, type FormEvent } from "react";
import { ApiError } from "@/lib/api";
import { Badge, Button, Dialog, Field, Input, Select } from "@/components/ui/primitives";
import { useCreateRma, useProducts, useUpdateRma, useVendors } from "./hooks";
import { RMA_NEXT, RMA_STATUS_LABELS, rmaTone } from "./labels";
import type { RmaDetail, RmaStatus } from "./api";

export function RmaDialog({
  open,
  onClose,
  existing,
}: {
  open: boolean;
  onClose: () => void;
  existing: RmaDetail | null;
}) {
  if (!open) return null;
  return existing ? <RmaDetailView rma={existing} onClose={onClose} /> : <RmaCreate onClose={onClose} />;
}

function RmaCreate({ onClose }: { onClose: () => void }) {
  const create = useCreateRma();
  const { data: products } = useProducts({ page_size: 200 }, { enabled: true });
  const { data: vendors } = useVendors({ page_size: 100 }, { enabled: true });

  const [productId, setProductId] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [serial, setSerial] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!productId || !reason.trim()) {
      setError("Pick a product and give a reason.");
      return;
    }
    try {
      await create.mutateAsync({
        product_id: productId,
        vendor_id: vendorId || null,
        quantity: Number(quantity) || 1,
        serial_number: serial.trim() || null,
        reason: reason.trim(),
      });
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not raise the RMA.");
    }
  }

  return (
    <Dialog open onClose={onClose} title="Raise an RMA">
      <form onSubmit={submit} className="space-y-3">
        {error && (
          <div role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}
        <Field label="Product" htmlFor="rma_product">
          <Select id="rma_product" value={productId} onChange={(e) => setProductId(e.target.value)}>
            <option value="">Select a product…</option>
            {(products?.items ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.sku})
              </option>
            ))}
          </Select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Vendor" htmlFor="rma_vendor">
            <Select id="rma_vendor" value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
              <option value="">None</option>
              {(vendors?.items ?? []).map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Quantity" htmlFor="rma_qty">
            <Input id="rma_qty" type="number" min="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
          </Field>
        </div>
        <Field label="Serial number" htmlFor="rma_serial">
          <Input id="rma_serial" value={serial} onChange={(e) => setSerial(e.target.value)} />
        </Field>
        <Field label="Reason" htmlFor="rma_reason">
          <Input id="rma_reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="What's wrong with it?" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={create.isPending}>
            Raise RMA
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function RmaDetailView({ rma, onClose }: { rma: RmaDetail; onClose: () => void }) {
  const update = useUpdateRma();
  const [note, setNote] = useState(rma.resolution_note ?? "");
  const [error, setError] = useState<string | null>(null);
  const next = RMA_NEXT[rma.status];

  async function transition(status: RmaStatus) {
    setError(null);
    try {
      await update.mutateAsync({ id: rma.id, body: { status, resolution_note: note.trim() || null } });
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not update the RMA.");
    }
  }

  return (
    <Dialog open onClose={onClose} title={rma.rma_number}>
      <div className="space-y-3">
        {error && (
          <div role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium">{rma.product_name}</span>
          <Badge tone={rmaTone(rma.status)}>{RMA_STATUS_LABELS[rma.status]}</Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          Qty {rma.quantity}
          {rma.serial_number ? ` · ${rma.serial_number}` : ""}
          {rma.vendor_name ? ` · ${rma.vendor_name}` : ""}
        </p>
        <div className="rounded-md bg-muted/50 p-2 text-sm">{rma.reason}</div>

        {next.length > 0 ? (
          <>
            <Field label="Resolution note" htmlFor="rma_note">
              <Input id="rma_note" value={note} onChange={(e) => setNote(e.target.value)} />
            </Field>
            <div className="flex flex-wrap justify-end gap-2 pt-1">
              {next.map((s) => (
                <Button
                  key={s}
                  variant={s === "rejected" ? "destructive" : "outline"}
                  disabled={update.isPending}
                  onClick={() => void transition(s)}
                >
                  Mark {RMA_STATUS_LABELS[s].toLowerCase()}
                </Button>
              ))}
            </div>
          </>
        ) : (
          <div className="flex justify-end">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        )}
      </div>
    </Dialog>
  );
}
